import React, { useEffect, useState } from "react";
import {
    TrendingUp, TrendingDown, Users, Building2, DollarSign, Activity,
    ShieldCheck, Briefcase, ChevronRight, ArrowUpRight, ArrowDownRight, Minus,
    Loader2, RefreshCw, Sparkles, MousePointerClick, Eye,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";

/** Colored trend indicator based on % change. */
const Trend = ({ pct }) => {
    if (pct === null || pct === undefined) {
        return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Minus className="h-3 w-3" /> n/a</span>;
    }
    const positive = pct > 0;
    const negative = pct < 0;
    const cls = positive ? "text-emerald-700" : negative ? "text-rose-700" : "text-slate-500";
    const Icon = positive ? ArrowUpRight : negative ? ArrowDownRight : Minus;
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`}>
            <Icon className="h-3 w-3" /> {Math.abs(pct)}%
        </span>
    );
};

const KpiCard = ({ icon: Icon, label, value, sub, trendPct, testId }) => (
    <div
        data-testid={testId}
        className="rounded-2xl border border-border bg-card p-5 shadow-sm"
    >
        <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
            <Icon className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="mt-3 flex items-baseline gap-2">
            <div className="text-2xl font-semibold text-foreground">{value}</div>
            {trendPct !== undefined && <Trend pct={trendPct} />}
        </div>
        {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
);

const Section = ({ title, subtitle, children, testId }) => (
    <section data-testid={testId} className="mt-10 first:mt-0">
        <div className="flex items-end justify-between mb-3">
            <div>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">{title}</h2>
                {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
        </div>
        {children}
    </section>
);

/** Simple funnel: signups → orgs → invites accepted → jobs → paid subs. */
const FunnelStrip = ({ f }) => {
    const stages = [
        { key: "signups_total",         label: "Signups",              value: f.signups_total },
        { key: "orgs_total",            label: "Employer Orgs",        value: f.orgs_total },
        { key: "invites_accepted_30d",  label: "Invites Accepted (30d)", value: f.invites_accepted_30d },
        { key: "jobs_posted_30d",       label: "Jobs Posted (30d)",    value: f.jobs_posted_30d },
        { key: "paid_subscribers",      label: "Paid Subscribers",     value: f.paid_subscribers },
    ];
    const max = Math.max(...stages.map((s) => s.value || 0), 1);
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="analytics-funnel">
            <div className="space-y-3">
                {stages.map((s, i) => {
                    const pct = (s.value / max) * 100;
                    return (
                        <div key={s.key} className="flex items-center gap-3">
                            <div className="w-40 shrink-0 text-xs font-medium text-muted-foreground">{s.label}</div>
                            <div className="flex-1">
                                <div className="h-8 rounded-md bg-muted overflow-hidden relative">
                                    <div
                                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
                                        style={{ width: `${Math.max(pct, 4)}%` }}
                                    />
                                    <span className="absolute inset-0 flex items-center px-3 text-xs font-semibold text-white mix-blend-difference">
                                        {s.value ?? 0}
                                    </span>
                                </div>
                            </div>
                            {i < stages.length - 1 && stages[i + 1].value > 0 && s.value > 0 && (
                                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/** Horizontal bars for permission distribution. */
const BarList = ({ data, colorMap = {} }) => {
    const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
    const max = Math.max(...entries.map(([, v]) => v), 1);
    if (entries.length === 0) return <div className="text-xs text-muted-foreground">No data yet.</div>;
    return (
        <div className="space-y-2">
            {entries.map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                    <div className="w-32 shrink-0 text-xs font-medium text-foreground truncate capitalize">
                        {k.replace(/_/g, " ")}
                    </div>
                    <div className="flex-1 h-4 rounded bg-muted overflow-hidden">
                        <div
                            className={`h-full rounded ${colorMap[k] || "bg-emerald-500"}`}
                            style={{ width: `${(v / max) * 100}%` }}
                        />
                    </div>
                    <div className="w-8 text-right text-xs font-semibold text-foreground">{v}</div>
                </div>
            ))}
        </div>
    );
};

export default function AnalyticsHub() {
    const [data, setData] = useState(null);
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snapshot, setSnapshot] = useState(null);
    const [nudge, setNudge] = useState(null);
    const [homepageJobs, setHomepageJobs] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [hub, boardResp, snap, ns, hpj] = await Promise.all([
                api.get("/admin/analytics-hub"),
                api.get("/admin/analytics/board").catch(() => ({ data: null })),
                api.get("/admin/attribution/snapshot").catch(() => ({ data: null })),
                api.get("/admin/attribution/nudge-stats").catch(() => ({ data: null })),
                api.get("/admin/analytics/homepage-jobs").catch(() => ({ data: null })),
            ]);
            setData(hub.data);
            setBoard(boardResp.data);
            setSnapshot(snap.data);
            setNudge(ns.data);
            setHomepageJobs(hpj.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading) return (
        <div className="py-24 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
        </div>
    );
    if (!data) return null;

    const { funnel, growth, team_composition, revenue, engagement, verifications, jobs } = data;

    return (
        <div data-testid="admin-analytics-hub">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Analytics Hub
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Every KPI an operator asks about — funnel, growth, revenue, engagement, pipeline health — in one view.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="analytics-refresh"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* Channel snapshot + verify-nudge conversion — top-of-page glance strip */}
            <div data-testid="channel-snapshot-strip" className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="card-soft p-5 lg:col-span-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.18em] text-violet-600 font-semibold">Top revenue channels · 30d</p>
                            <p className="text-xs text-muted-foreground mt-0.5">First-touch attribution</p>
                        </div>
                        <a href="/admin/attribution" className="text-xs text-emerald-600 hover:underline font-medium" data-testid="attr-open-full">Full report →</a>
                    </div>
                    <div className="mt-4 space-y-2">
                        {(snapshot?.top_channels || []).length === 0 && (
                            <p className="text-sm text-muted-foreground py-4">No attributed revenue in the last 30 days yet.</p>
                        )}
                        {(snapshot?.top_channels || []).map((c, i) => {
                            const max = Math.max(...(snapshot.top_channels.map((x) => x.revenue || 0)), 1);
                            const pct = Math.round(((c.revenue || 0) / max) * 100);
                            return (
                                <div key={c.utm_source} data-testid={`snapshot-row-${c.utm_source}`} className="flex items-center gap-3">
                                    <div className="w-6 text-xs text-muted-foreground font-mono">#{i + 1}</div>
                                    <div className="w-28 text-sm font-medium text-foreground truncate">{c.display_name || c.utm_source}</div>
                                    <div className="flex-1 h-2 bg-muted/40 rounded-full overflow-hidden">
                                        <div className="h-2 bg-gradient-to-r from-violet-500 to-emerald-500" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="w-20 text-right text-sm font-semibold text-emerald-600 mono-score">
                                        ${(c.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </div>
                                    <div className="w-14 text-right text-xs text-muted-foreground">{c.paying} pays</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div data-testid="nudge-tile" className="card-soft p-5">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-600 font-semibold">Verify-nudge conversion</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Sent → ID verified</p>
                    <div className="mt-3">
                        <div className="font-heading text-3xl font-semibold text-foreground mono-score" data-testid="nudge-conv-pct">
                            {nudge?.combined?.conversion_pct ?? 0}%
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            <span className="font-semibold text-foreground mono-score">{nudge?.combined?.verified ?? 0}</span> verified of <span className="font-semibold text-foreground mono-score">{nudge?.combined?.sent ?? 0}</span> nudges
                        </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-border space-y-1 text-xs text-muted-foreground">
                        <div className="flex justify-between"><span>Applicant drip</span><span className="mono-score text-foreground">{nudge?.initial_applicant_nudge?.verified ?? 0} / {nudge?.initial_applicant_nudge?.sent ?? 0}</span></div>
                        <div className="flex justify-between"><span>Passport-view nudge</span><span className="mono-score text-foreground">{nudge?.employer_passport_view_nudge?.verified ?? 0} / {nudge?.employer_passport_view_nudge?.sent ?? 0}</span></div>
                    </div>
                </div>
            </div>

            {/* Public Board Activity — NEW analytics section */}
            {board && (
                <Section title="Public Board Activity" subtitle="Applications trend, top employers, and view → apply conversion" testId="analytics-section-board">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
                        <KpiCard icon={Activity} label="Views — 30d" value={board.funnel.views_30d} sub={`${board.funnel.views_all_time} all-time`} testId="kpi-views" />
                        <KpiCard icon={Briefcase} label="Applications — 30d" value={board.funnel.applications_30d} sub={`${board.funnel.applications_all_time} all-time`} testId="kpi-apps-30d" />
                        <KpiCard icon={TrendingUp} label="View → Apply (30d)" value={board.funnel.conversion_pct_30d !== null ? `${board.funnel.conversion_pct_30d}%` : (board.funnel.insufficient_view_data_30d ? "—" : "—")} sub={board.funnel.insufficient_view_data_30d ? "Collecting view data…" : (board.funnel.conversion_pct_all_time !== null ? `${board.funnel.conversion_pct_all_time}% all-time` : "")} testId="kpi-conv-30d" />
                        <KpiCard icon={Sparkles} label="Featured Jobs" value={board.featured_jobs_active} sub="Currently promoted" testId="kpi-featured" />
                    </div>

                    {/* Weekly bars — inline SVG, no library dependency. */}
                    <div className="rounded-2xl border border-border bg-card p-5" data-testid="analytics-weekly-chart">
                        <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Applications per week (last 12)</h3>
                        {(() => {
                            const rows = board.applications_by_week || [];
                            const max = Math.max(...rows.map((r) => r.count), 1);
                            return (
                                <div className="flex items-end gap-1.5 h-40">
                                    {rows.map((r) => {
                                        const pct = (r.count / max) * 100;
                                        return (
                                            <div key={r.week} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group" title={`${r.week}: ${r.count}`}>
                                                <div className="text-[10px] text-foreground font-semibold">{r.count > 0 ? r.count : ""}</div>
                                                <div
                                                    className="w-full rounded-t bg-gradient-to-b from-emerald-400 to-emerald-600 min-h-[3px] transition-all"
                                                    style={{ height: `${pct}%` }}
                                                    data-testid={`bar-${r.week}`}
                                                />
                                                <div className="text-[9px] text-muted-foreground whitespace-nowrap">{r.week.split("-").slice(1).join("-")}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>

                    {/* Top employers */}
                    <div className="mt-4 rounded-2xl border border-border bg-card p-5" data-testid="analytics-top-employers">
                        <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Top employers by applications</h3>
                        {(board.top_employers || []).length === 0 ? (
                            <div className="text-xs text-muted-foreground">No applications yet.</div>
                        ) : (
                            <div className="space-y-2">
                                {board.top_employers.map((e) => {
                                    const max = board.top_employers[0].applications || 1;
                                    return (
                                        <div key={e.company_slug} className="flex items-center gap-3">
                                            <div className="w-40 shrink-0 text-xs font-medium text-foreground truncate">{e.company}</div>
                                            <div className="flex-1 h-4 rounded bg-muted overflow-hidden">
                                                <div className="h-full rounded bg-emerald-500" style={{ width: `${(e.applications / max) * 100}%` }} />
                                            </div>
                                            <div className="w-24 text-right text-xs font-semibold text-foreground">
                                                {e.applications} <span className="text-muted-foreground font-normal">on {e.jobs} job{e.jobs === 1 ? "" : "s"}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {/* Onboarding funnel */}
            <Section title="Onboarding Funnel" subtitle="How prospects flow from signup to first paid subscription" testId="analytics-section-funnel">
                <FunnelStrip f={funnel} />
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <KpiCard icon={Users}     label="Total Signups"        value={funnel.signups_total} sub={`+${funnel.signups_7d} last 7 days`} testId="kpi-signups-total" />
                    <KpiCard icon={Building2} label="Employer Orgs"        value={funnel.orgs_total}    sub={`+${funnel.orgs_30d} last 30 days`}  testId="kpi-orgs" />
                    <KpiCard icon={ShieldCheck} label="Invite Accept Rate" value={funnel.invite_accept_rate_pct !== null ? `${funnel.invite_accept_rate_pct}%` : "—"} sub={`${funnel.invites_accepted_30d}/${funnel.invites_sent_30d} last 30d`} testId="kpi-accept-rate" />
                    <KpiCard icon={DollarSign} label="Paid Subscribers"    value={funnel.paid_subscribers} testId="kpi-paid-subs" />
                </div>
            </Section>

            {/* Growth */}
            <Section title="Growth" subtitle="30-day trailing vs. prior 30-day window" testId="analytics-section-growth">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <KpiCard icon={TrendingUp} label="Signups — 30d" value={growth.signups_30d} sub={`vs. ${growth.signups_prev_30d} prior`} trendPct={growth.signups_growth_pct} testId="kpi-growth-signups" />
                    <KpiCard icon={Building2} label="New Orgs — 30d" value={growth.orgs_30d} sub={`vs. ${growth.orgs_prev_30d} prior`} trendPct={growth.orgs_growth_pct} testId="kpi-growth-orgs" />
                    <KpiCard icon={Users} label="Talent Accounts" value={growth.users_by_role.professional} testId="kpi-talent" />
                    <KpiCard icon={Users} label="Employer Users"  value={growth.users_by_role.employer}     testId="kpi-employer-users" />
                </div>
            </Section>

            {/* Revenue */}
            <Section title="Revenue" subtitle="MRR + conversion + churn" testId="analytics-section-revenue">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <KpiCard icon={DollarSign} label="MRR" value={`$${revenue.mrr_dollars.toLocaleString()}`} testId="kpi-mrr" />
                    <KpiCard icon={TrendingUp} label="Free → Paid (30d)" value={revenue.free_to_paid_30d_pct !== null ? `${revenue.free_to_paid_30d_pct}%` : "—"} sub={`${revenue.free_to_paid_30d_numer}/${revenue.free_to_paid_30d_denom} new orgs`} testId="kpi-conversion" />
                    <KpiCard icon={TrendingDown} label="Churn — 30d" value={revenue.churn_30d} sub={`${revenue.churn_7d} in last 7d`} testId="kpi-churn" />
                    <KpiCard icon={TrendingDown} label="Failed Payments — 30d" value={revenue.failed_payments_30d} testId="kpi-failed-payments" />
                </div>
                {Object.keys(revenue.plan_distribution || {}).length > 0 && (
                    <div className="mt-4 rounded-2xl border border-border bg-card p-5">
                        <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Active plans</h3>
                        <BarList data={revenue.plan_distribution} />
                    </div>
                )}
            </Section>

            {/* Team composition */}
            <Section title="Team Composition" subtitle="How employer orgs are structured" testId="analytics-section-team">
                <div className="grid gap-3 md:grid-cols-3 mb-3">
                    <KpiCard icon={Building2} label="Total Orgs"            value={team_composition.total_orgs}          testId="kpi-tc-orgs" />
                    <KpiCard icon={Users}     label="Total Employer Users"  value={team_composition.total_employer_users} testId="kpi-tc-users" />
                    <KpiCard icon={Users}     label="Avg. Team Size"        value={team_composition.avg_team_size}       testId="kpi-tc-avg" />
                </div>
                <div className="rounded-2xl border border-border bg-card p-5">
                    <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Permission distribution</h3>
                    <BarList
                        data={team_composition.permission_distribution}
                        colorMap={{
                            owner: "bg-emerald-500",
                            admin: "bg-sky-500",
                            recruiter: "bg-violet-500",
                            hiring_manager: "bg-amber-500",
                            interviewer: "bg-slate-500",
                        }}
                    />
                </div>
            </Section>

            {/* Engagement */}
            <Section title="Engagement" subtitle="Distinct-actor activity over the last 7 days (from audit log)" testId="analytics-section-engagement">
                <div className="grid gap-3 md:grid-cols-2">
                    <KpiCard icon={Activity} label="Distinct Actors — 7d" value={engagement.distinct_actors_7d} testId="kpi-actors" />
                    <div className="rounded-2xl border border-border bg-card p-5">
                        <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Top actions — 7d</h3>
                        {engagement.top_actions_7d.length === 0 ? (
                            <div className="text-xs text-muted-foreground">No activity in the last 7 days.</div>
                        ) : (
                            <div className="space-y-1.5">
                                {engagement.top_actions_7d.map((a) => (
                                    <div key={a.action} className="flex items-center justify-between text-xs">
                                        <span className="font-mono text-muted-foreground truncate">{a.action}</span>
                                        <span className="font-semibold text-foreground">{a.count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Section>

            {/* Pipeline health */}
            <Section title="Pipeline Health" subtitle="Verifications + jobs in the queue" testId="analytics-section-pipeline">
                <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border border-border bg-card p-5">
                        <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Verifications
                        </h3>
                        <BarList data={verifications.by_status} />
                        <div className="mt-3 text-xs text-muted-foreground">{verifications.requested_30d} requested in last 30 days</div>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-5">
                        <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Briefcase className="h-3.5 w-3.5 text-emerald-600" /> Jobs
                        </h3>
                        <BarList data={jobs.by_status} />
                    </div>
                </div>
            </Section>

            {/* Homepage Jobs — top 10 clicked in the last 7 days from the
                privacy-safe /api/track beacon. Includes a small funnel strip
                so the operator can eyeball view → click → apply drop. */}
            {homepageJobs && (
                <Section
                    title="Homepage Jobs — Last 7 Days"
                    subtitle="Top clicked jobs from the /  homepage + funnel snapshot"
                    testId="analytics-section-homepage-jobs"
                >
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 mb-4">
                        <KpiCard icon={Eye} label="Home views" value={homepageJobs.funnel.homepage_views} testId="kpi-hpj-views" />
                        <KpiCard icon={Eye} label="Card impressions" value={homepageJobs.funnel.homepage_impressions ?? 0} sub="≥ 50% visible" testId="kpi-hpj-impressions" />
                        <KpiCard icon={MousePointerClick} label="Home clicks" value={homepageJobs.funnel.homepage_clicks} testId="kpi-hpj-clicks" />
                        <KpiCard icon={Briefcase} label="Job page views" value={homepageJobs.funnel.job_page_views} testId="kpi-hpj-jobviews" />
                        <KpiCard icon={Activity} label="Apply starts" value={homepageJobs.funnel.apply_starts} testId="kpi-hpj-applies" />
                        <KpiCard icon={Sparkles} label="Passport CTAs" value={homepageJobs.funnel.passport_ctas} testId="kpi-hpj-passport" />
                    </div>
                    {homepageJobs.top_jobs.length === 0 ? (
                        <div className="card-soft p-6 text-sm text-muted-foreground text-center" data-testid="hpj-empty">
                            No homepage job clicks yet in the last 7 days. The section is either running with the empty-state, or the site has just been seeded with jobs.
                        </div>
                    ) : (
                        <div className="card-soft p-3" data-testid="hpj-top-list">
                            {homepageJobs.top_jobs.map((r, i) => {
                                const max = Math.max(...homepageJobs.top_jobs.map((x) => x.clicks), 1);
                                const pct = Math.round((r.clicks / max) * 100);
                                const stale = r.status !== "published" || !r.is_active;
                                return (
                                    <div
                                        key={r.job_id || i}
                                        data-testid={`hpj-row-${r.job_id}`}
                                        className="flex items-center gap-3 py-2 border-b border-border last:border-b-0"
                                    >
                                        <div className="w-6 text-xs text-muted-foreground font-mono">#{i + 1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                {r.title === "(removed)" ? (
                                                    <span className="text-sm font-medium text-muted-foreground italic truncate">Deleted job</span>
                                                ) : (
                                                    <Link
                                                        to={`/careers/${r.slug}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-sm font-medium text-foreground truncate hover:underline"
                                                        data-testid={`hpj-title-${r.job_id}`}
                                                    >
                                                        {r.title}
                                                    </Link>
                                                )}
                                                {stale && r.title !== "(removed)" && (
                                                    <span className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-700 border border-amber-500/30 px-1.5 py-0.5 rounded-full">
                                                        {r.status}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground truncate">
                                                {[r.company, r.location].filter(Boolean).join(" · ") || "—"}
                                            </div>
                                        </div>
                                        <div className="w-32 h-2 bg-muted/40 rounded-full overflow-hidden">
                                            <div className="h-2 bg-gradient-to-r from-sky-500 to-emerald-500" style={{ width: `${pct}%` }} />
                                        </div>
                                        <div className="w-16 text-right text-sm font-semibold text-emerald-600 mono-score">
                                            {r.clicks}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Section>
            )}

            <p className="mt-8 text-center text-[11px] text-muted-foreground">
                Snapshot generated at {new Date(data.generated_at).toLocaleString()}
            </p>
        </div>
    );
}
